import React, { useState } from "react";
import { useMsal } from "@azure/msal-react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import PropTypes from "prop-types";
import axios from "axios";

const QAFeedback = ({ show, onHide, job, jobId, manualMode = false }) => {
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [manualJobNumber, setManualJobNumber] = useState("");
  const { instance } = useMsal();

  const jobNumber = manualMode ? manualJobNumber : (job?.JobNumber || "");
  const projectName = manualMode ? "Manual Feedback" : (job?.ProjectName || "");
  const mechanicalEngineer = job?.MechanicalEngineer || "";
  const electricalEngineer = job?.ElectricalEngineer || "";
  const salesQuoteID = job?.SalesQuoteID || null;

  // Validation for manual mode: check if work order number starts with 202 (7 digits) or MO
  const isInvalidWorkOrder = manualMode && manualJobNumber && (
    /^202\d{4}$/.test(manualJobNumber) || // 7 digits starting with 202 (job numbers)
    /^MO/i.test(manualJobNumber) // Starts with MO (case insensitive)
  );

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files));
  };

  const resetForm = () => {
    setSubject("");
    setDescription("");
    setFiles([]);
    setManualJobNumber("");
    setSubmitError(null);
    setSubmitSuccess(false);
    // Reset file input
    const fileInput = document.getElementById("qaFeedbackFile");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate JobNumber and JobName before proceeding
    if (!jobNumber || jobNumber === "" || !projectName || projectName === "") {
      setSubmitError("Missing job information. Please try again.");
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);
      setSubmitSuccess(false);

      const account = instance.getAllAccounts()[0];
      if (!account) {
        setSubmitError("Authentication failed. Please log in again.");
        setIsSubmitting(false);
        return;
      }

      const request = { scopes: ["https://watertronics-my.sharepoint.com/.default"], account };
      const tokenResponse = await instance.acquireTokenSilent(request);
      const accessToken = tokenResponse.accessToken;

      const siteUrl = process.env.REACT_APP_SHAREPOINT_SITE_URL;
      const listName = process.env.REACT_APP_QAFEEDBACK_LIST_NAME;

      // Fetch correct metadata type for SharePoint
      const metadataRes = await axios.get(
        `${siteUrl}/_api/web/lists/getbytitle('${listName}')?$select=ListItemEntityTypeFullName`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json;odata=verbose"
          }
        }
      );
      
      const metadata = metadataRes.data;
      const listItemEntityType = metadata.d.ListItemEntityTypeFullName;

      // Create payload - using exact same structure as JobQa.js
      const payload = {
        __metadata: { type: listItemEntityType },
        Title: subject,
        Comments: description,
        JobNumber: jobNumber,
        JobName: projectName,
        SaleQuoteID: salesQuoteID,
        Mechanical: mechanicalEngineer,
        Electrical: electricalEngineer,
        ProjectID: jobId || null
      };

      // Create a new SharePoint list item
      const response = await axios.post(
        `${siteUrl}/_api/web/lists/getbytitle('${listName}')/items`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json;odata=verbose",
            "Content-Type": "application/json;odata=verbose"
          }
        }
      );
      
      const newItem = response.data;

      // Upload attachments if files exist
      if (files.length > 0) {
        const itemId = newItem.d.ID;

        for (const file of files) {
          const fileName = encodeURIComponent(file.name);

          const uploadResponse = await fetch(
            `${siteUrl}/_api/web/lists/getbytitle('${listName}')/items(${itemId})/AttachmentFiles/add(FileName='${fileName}')`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${accessToken}`,
                Accept: "application/json;odata=verbose"
              },
              body: file
            }
          );

          if (!uploadResponse.ok) {
            const errorDetails = await uploadResponse.text();
            throw new Error(`Error uploading file: ${uploadResponse.status} - ${errorDetails}`);
          }
        }
      }

      // Show success message
      setSubmitSuccess(true);
      setIsSubmitting(false);
      
      // Reset form after short delay and close modal
      setTimeout(() => {
        resetForm();
        onHide();
      }, 1500);

    } catch (error) {
      console.error("Error submitting QA feedback:", error);
      setSubmitError(error.message || "Failed to submit feedback. Please try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <Modal className="job-schedule-modal" show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-clipboard-check me-2"></i>
          {manualMode ? "Submit QA Feedback" : `Submit QA Feedback - ${jobNumber}`}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {manualMode && (
            <Alert variant="danger" className="mb-3">
              <small>
                <em>
                  <strong>Note:</strong> This is for submitting feedback for work order, part orders, vendor order, etc. 
                  Please use the schedule or portal to submit feedback for Jobs or MO's.
                </em>
              </small>
            </Alert>
          )}
          
          {!manualMode && projectName && (
            <div className="mb-3">
              <strong>Project:</strong> {projectName}
            </div>
          )}

          {submitError && (
            <Alert variant="danger" onClose={() => setSubmitError(null)} dismissible>
              {submitError}
            </Alert>
          )}

          {submitSuccess && (
            <Alert variant="success">
              <i className="bi bi-check-circle me-2"></i>
              Feedback submitted successfully!
            </Alert>
          )}

          {manualMode && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Work Order # * </Form.Label>
                <Form.Control
                  type="text"
                  value={manualJobNumber}
                  onChange={(e) => setManualJobNumber(e.target.value)}
                  required
                  disabled={isSubmitting}
                  placeholder="Enter work order number"
                  className={isInvalidWorkOrder ? "is-invalid" : ""}
                />
                {isInvalidWorkOrder ? (
                  <Form.Text className="text-danger">
                    <i className="bi bi-exclamation-triangle-fill me-1"></i>
                    QA feedback for Jobs (20XX) and MO's must go through the Portal or Schedule
                  </Form.Text>
                ) : (
                  <Form.Text className="text-muted">
                    Enter the work order number this feedback relates to
                  </Form.Text>
                )}
              </Form.Group>
            </>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Subject *</Form.Label>
            <Form.Control
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              disabled={isSubmitting}
              placeholder="Brief description of the issue or feedback"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description *</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              disabled={isSubmitting}
              placeholder="Detailed description of the quality issue or feedback"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Attachments</Form.Label>
            <Form.Control
              type="file"
              id="qaFeedbackFile"
              multiple
              onChange={handleFileChange}
              disabled={isSubmitting}
            />
            <Form.Text className="text-muted">
              You can attach multiple files (images, documents, etc.)
            </Form.Text>
            {files.length > 0 && (
              <div className="mt-2">
                <small>
                  <strong>Selected files:</strong>
                  <ul className="mb-0">
                    {files.map((file, index) => (
                      <li key={index}>{file.name}</li>
                    ))}
                  </ul>
                </small>
              </div>
            )}
          </Form.Group>

          {isSubmitting && (
            <div className="text-center my-3">
              <Spinner animation="border" variant="primary" role="status">
                <span className="visually-hidden">Submitting...</span>
              </Spinner>
              <p className="mt-2">Submitting your feedback...</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit" 
            disabled={isSubmitting || !subject || !description || (manualMode && (!manualJobNumber || isInvalidWorkOrder))}
          >
            {isSubmitting ? "Submitting..." : "Submit Feedback"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

QAFeedback.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  job: PropTypes.object,
  jobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  manualMode: PropTypes.bool,
};

export default QAFeedback;
